import { RedirectToSignIn, SignedIn, SignedOut, UserButton } from "@clerk/clerk-react";
import type { QueryClient } from "@tanstack/react-query";
import {
  Link,
  Outlet,
  createRootRouteWithContext,
  type ErrorComponentProps,
} from "@tanstack/react-router";
import { Button, Heading } from "@panelgenie/shared-ui";
import { BuildBadge } from "../../components/BuildBadge.js";
import type { TrpcClient } from "../../trpc/client.js";

/**
 * Root layout for the staff support console.
 *
 * The auth gate lives here so both routes (list + detail) are behind a
 * signed-in session. Note the gate the UI shows is *cosmetic* — the
 * real control is the server's `staffProcedure` (Clerk
 * `public_metadata.role === "staff"`). A signed-in non-staff user
 * reaches the surface but every `support.*` query comes back FORBIDDEN,
 * which the route surfaces as an access banner.
 */
export type RouterContext = {
  readonly trpc: TrpcClient;
  readonly queryClient: QueryClient;
  /**
   * Clerk dashboard deep-link template (`…/users/{userId}`) for the
   * invocation list's User column, or `undefined` when unconfigured.
   */
  readonly clerkDashboardUserUrlTemplate: string | undefined;
};

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  errorComponent: RootErrorComponent,
});

function RootLayout() {
  return (
    <div className="flex h-screen flex-col overflow-hidden bg-surface text-fg">
      <header
        aria-label="app-chrome"
        className="z-10 flex items-center gap-3 border-b border-line bg-elevated px-4 py-3"
      >
        <Link to="/" className="font-display text-lg font-semibold text-fg">
          PanelGenie <span className="text-accent">Support</span>
        </Link>
        <span className="rounded-pill border border-line px-2 py-0.5 text-xs text-mute">
          staff
        </span>
        <nav className="ml-3 flex items-center gap-4 text-sm">
          <Link
            to="/"
            className="text-mute hover:text-fg [&.active]:text-fg [&.active]:font-medium"
          >
            Invocations
          </Link>
          <Link
            to="/test-cases"
            className="text-mute hover:text-fg [&.active]:text-fg [&.active]:font-medium"
          >
            Test cases
          </Link>
        </nav>
        <div className="flex-1" />
        <BuildBadge />
        <SignedIn>
          <UserButton />
        </SignedIn>
      </header>
      <main className="flex w-full min-h-0 flex-1 flex-col overflow-hidden">
        <SignedIn>
          <Outlet />
        </SignedIn>
        <SignedOut>
          <RedirectToSignIn />
        </SignedOut>
      </main>
    </div>
  );
}

function RootErrorComponent({ error, reset }: ErrorComponentProps) {
  return (
    <div role="alert" className="mx-auto max-w-xl p-6">
      <Heading level="h1" className="mb-2">
        Something went wrong.
      </Heading>
      <p className="mb-4 text-mute">{error.message}</p>
      <Button onClick={reset}>Try again</Button>
    </div>
  );
}

export { RootErrorComponent };
